import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PortalRegistrationState as MyleModelsPortalPortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { AccountSendInvitation } from "./Management/AccountInvitation";

interface IPortalRegistrationProps {
    patientInfo: Patient;
    onCloseDelegate?: () => void;
    toggleHideActionsDelegate?: () => void;
    toggleSendButton?: IToggleButtonFunction;
    showRegistrationConfirmation?: boolean;
    showAsPopup: boolean;
    portalButtonStateCallback?: (portalState: MyleModelsPortalPortalRegistrationState) => void;
}

interface IToggleButtonFunction {
    (isEnabled: boolean, isSuccess?: boolean): void;
}

interface ISetRelinkDelegateFunction {
    (registerFunction: () => void, refuseFunction: () => void): void;
}

class PortalRegistrationState {
    public isRequestFailed: boolean;
    public errors: string[];

    public registerFunctionDelegate: () => void;
    public refuseFunctionDelegate: () => void;
    public closeFunctionDelegate: () => void;

    public isSendDisabled: boolean;
    public isRefuseDisabled: boolean;
    public hideActions: boolean;

    public constructor(s?: PortalRegistrationState) {
        if (s != null) {
            this.isRequestFailed = s.isRequestFailed;
            this.errors = s.errors;
            this.registerFunctionDelegate = s.registerFunctionDelegate;
            this.refuseFunctionDelegate = s.refuseFunctionDelegate;
            this.closeFunctionDelegate = s.closeFunctionDelegate;
            this.isSendDisabled = s.isSendDisabled;
            this.isRefuseDisabled = s.isRefuseDisabled;
            this.hideActions = s.hideActions;
        }
    }
}

class PortalRegisterControl extends React.Component<IPortalRegistrationProps, PortalRegistrationState> {
    private localDispatcherReference: any;

    constructor(props: IPortalRegistrationProps) {
        super(props);

        const initialState = new PortalRegistrationState();
        this.state = initialState;
    }

    public componentDidMount(): void {
        sessionStore.showGlobalOverlay();

        $("#Portal_Register_Control").focus();
        document.addEventListener("keyup", e => this.closeOnEscape(e));

        this.localDispatcherReference = LocalEventDispatcher.register(p => this.onDispatchEvent(p));
    }

    public componentWillUnmount(): void {
        LocalEventDispatcher.unregister(this.localDispatcherReference);
        sessionStore.hideGlobalOverlay();
        document.removeEventListener("keyup", e => this.closeOnEscape(e));
    }

    public render(): JSX.Element {
        const birthDate = this.props.patientInfo.IndividualResponse.DateOfBirth;

        const isOfAge = birthDate && PatientHelper.getPatientIsOfAgeOfConsent(birthDate);

        return (
            <div id="Portal_Register_Control">
                <div className={`${this.props.showAsPopup ? "portal-register-modal " : " "}account-invitation`}>
                    <h3 className="header thin-font">{cacheStore.GetString("PortalRegistrationLabel")}</h3>
                    {isOfAge
                        ? this.renderRegistration()
                        : this.renderUnderAge(birthDate)
                    }
                </div>
            </div>

        );
    }

    private renderUnderAge(birthDate: Date): JSX.Element {
        return (
            <div>
                <div className={this.props.showAsPopup ? "content" : ""} style={{ paddingTop: "7px", paddingBottom: "7px" }}>
                    <h5 style={{ lineHeight: "normal" }}>
                        {birthDate ? cacheStore.GetString("Under14MessageLabel") : cacheStore.GetString("EnterDateOfBirthPortalErrorMessage")}
                    </h5>
                </div>
                {this.props.showAsPopup && (
                    <div className="footer clearfix">
                        <div className="pull-right">
                            <button className="btn btn-default" onClick={() => this.closeForm()}>
                                <span>{cacheStore.GetString("CloseLabel")}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderRegistration(): JSX.Element {
        return (
            <div>
                <AccountSendInvitation
                    patientInfo={this.props.patientInfo}
                    setDelegatesFunction={(registerDelegate, refuseDelegate) => this.setDelegates(registerDelegate, refuseDelegate)}
                    onCloseDelegate={() => this.closeForm()}
                    onToggleSendDelegate={isDisabled => this.toggleSendButton(isDisabled)}
                    onToggleRefuseDelegate={isDisabled => this.handleRefuseButtonResponse(isDisabled)}
                    onToggleHideActionsDelegate={isDisabled => this.toggleActionsVisibility(isDisabled)}
                />
                {this.renderActionsBtn()}
            </div>
        );
    }

    private renderActionsBtn(): JSX.Element {
        if (!this.props.showAsPopup) {
            return this.renderButtonsForContentOnly();
        }

        return this.renderButtonsForPopup();
    }

    private renderButtonsForContentOnly(): JSX.Element {
        if (this.props.showRegistrationConfirmation) {
            return <div />;
        }

        return (
            <div className="footer clearfix">
                <div className="pull-right">
                    <button className="btn btn-primary" onClick={() => this.sendRegisterRequest()}>
                        <span>{cacheStore.GetString("SendInvitationLabel")}</span>
                    </button>
                </div>
            </div>
        );
    }

    private renderButtonsForPopup(): JSX.Element {
        let btns = [];
        btns.push(
            <button key="btn-cancel" className="btn btn-default push-right-5" onClick={() => this.closeForm()}>
                <span>{cacheStore.GetString("CloseLabel")}</span>
            </button>
        );

        if (!this.state.hideActions) {
            btns.push(
                <button key="btn-refuse" className="btn btn-primary push-right-5" onClick={() => this.refusePortal()}>
                    <span>{cacheStore.GetString("RefuseLabel")}</span>
                </button>
            );

            btns.push(
                <button key="btn-register" className="btn btn-primary" onClick={() => this.sendRegisterRequest()} disabled={this.state.isSendDisabled}>
                    <span>{cacheStore.GetString("SendLabel")}</span>
                </button>
            );
        }

        return (
            <div className="footer clearfix">
                <div className="pull-right">
                    {btns}
                </div>
            </div>
        );
    }

    private setDelegates(registerDelegate: () => void, refuseDelegate: () => void): void {
        let self = this;
        let s = new PortalRegistrationState(self.state);
        s.registerFunctionDelegate = registerDelegate.bind(this);
        s.refuseFunctionDelegate = refuseDelegate.bind(this);
        self.setState(s);
    }

    private closeForm(): void {
        if (this.props.onCloseDelegate != null) {
            this.props.onCloseDelegate();
        }
    }

    private refusePortal(): void {
        if (this.state.refuseFunctionDelegate != null) {
            this.state.refuseFunctionDelegate();
        }
    }

    private sendRegisterRequest(): void {
        if (this.state.registerFunctionDelegate != null) {
            this.state.registerFunctionDelegate();
        }
    }

    private toggleSendButton(isDisabled: boolean): void {
        var s = new PortalRegistrationState(this.state);
        s.isSendDisabled = isDisabled;
        this.setState(s);
    }

    private handleRefuseButtonResponse(isDisabled: boolean): void {
        var s = new PortalRegistrationState(this.state);
        s.isRefuseDisabled = isDisabled;
        this.setState(s);

        if (isDisabled && this.props.portalButtonStateCallback != null)
            this.props.portalButtonStateCallback(MyleModelsPortalPortalRegistrationState.PortalRefused);
    }

    private toggleActionsVisibility(isDisabled: boolean): void {
        var s = new PortalRegistrationState(this.state);
        s.hideActions = isDisabled;
        this.setState(s);
    }

    private closeOnEscape(e: KeyboardEvent): void {
        if (e.keyCode == 27) {
            this.closeForm();
        }
    }

    private onDispatchEvent(event: LocalEvent): void {
        if (event.Operation === LocalOperations.Misc.MyleApp_Container_Click) {
            window.setTimeout(() => {
                this.closeForm();
            }, 50);
        }
    }
}

export { ISetRelinkDelegateFunction, IToggleButtonFunction, PortalRegisterControl, PortalRegistrationState };
